import React from 'react';
import VisaPageLayout, { VisaPlan, RequirementItem, ProcessStep, FaqItem } from './/visa-page-layout';
import { useVisaPacksByCountry } from '../hooks/useVisaPacksByCountry';


const plans: VisaPlan[] = [
  { id: 'am-check', name: 'Entry Requirement Check', entryType: 'Single Entry', validity: 'Aligned to your travel dates', stay: 'Up to 180 days in Armenia', processing: 'Same day confirmation', price: '8', currency: 'OMR' },
  { id: 'am-evisa', name: 'Tourist eVisa', entryType: 'Single Entry', validity: 'Use within 120 days of issue', stay: 'Up to 120 days in Armenia', processing: '2–3 business days', price: '25', currency: 'OMR', featured: true },
  { id: 'am-multi', name: 'Tourist eVisa', entryType: 'Multiple Entry', validity: 'Valid for 1 year from issue', stay: 'Up to 120 days per visit', processing: '3–5 business days', price: '45', currency: 'OMR' },
];

const requirements: RequirementItem[] = [
  { label: 'Passport copy', detail: 'Valid for at least 6 months from your arrival date.' },
  { label: 'Passport-size photo', detail: 'Recent, plain background, matching standard visa specifications.' },
  { label: 'Confirmed return ticket', detail: 'Round-trip or onward flight booking showing your departure from Armenia.' },
  { label: 'Hotel booking or host details', detail: 'Confirmed accommodation, or your host\'s invitation letter if staying privately.' },
  { label: 'Proof of funds (if requested)', detail: 'Occasionally requested for certain nationalities as part of standard review.' },
];

const goodToKnow: string[] = [
  'Many nationalities can enter Armenia visa-free or receive a visa on arrival — we\'ll confirm whether you need to apply for anything before you pay for a service.',
  'Armenia pairs naturally with a wider Caucasus itinerary — ask us about combining it with Georgia in one trip.',
  'The multiple-entry option suits travelers doing a Caucasus loop that returns to Armenia more than once.',
  'Some border crossings by land have different rules to arrival by air — tell us your route and we\'ll flag anything unusual.',
];

const steps: ProcessStep[] = [
  { title: 'Tell us your travel plan', text: 'Share your nationality, dates and entry point so we can confirm the right route.' },
  { title: 'We review & submit', text: 'For the eVisa route, our team checks your documents before filing with Armenian authorities.' },
  { title: 'Track your status', text: 'We\'ll keep you updated by WhatsApp or email while your application is processed.' },
  { title: 'Travel with confidence', text: 'Arrive with your approved eVisa or entry confirmation already settled.' },
];

const faqs: FaqItem[] = [
  { question: 'Do I need a visa to visit Armenia?', answer: 'It depends on your passport — many nationalities can enter visa-free or get a visa on arrival. Send us your nationality and we\'ll tell you honestly what, if anything, you need to arrange in advance.' },
  { question: 'Can I combine Armenia with a trip to Georgia?', answer: 'Yes, this is a popular combination and the land border crossing is straightforward for most travelers. Tell us your full route and we\'ll confirm requirements for both countries.' },
  { question: 'How long can tourists typically stay in Armenia?', answer: 'This varies by nationality and entry type, generally ranging from 120 to 180 days. We\'ll confirm the exact allowance for your situation.' },
  { question: 'What should I know about visiting the Tatev Monastery and other remote sites?', answer: 'No special permits are needed for standard tourist sites, including Tatev — just plan extra travel time given Armenia\'s mountainous terrain. We can help build that into your itinerary.' },
];

export default function VisaArmeniaPage() {
  const { plans, loading, error } = useVisaPacksByCountry('ARM');
  return (
    <VisaPageLayout
      countryName="Armenia"
      flagEmoji="🇦🇲"
      heroEyebrow="Republic of Armenia"
      heroImage="https://images.unsplash.com/photo-1589537368054-6a9c11ba1eac?w=1600&q=80&fit=crop&crop=center"
      heroTitleLead="Your"
      heroTitleHighlight="Armenia"
      heroTitleTail="entry, confirmed before you fly"
      heroSubtitle="From Yerevan's Cascade to the cliffside monasteries of Tatev — we confirm exactly what your Armenia trip needs, and prepare your eVisa if one applies to you."
      trustStats={[
        { value: '2–5', label: 'Business Days' },
        { value: '96%', label: 'Approval Rate' },
        { value: '1,100+', label: 'Armenia Travelers Assisted' },
      ]}
      plans={plans}
      plansLoading={loading}
      plansError={error}
      requirements={requirements}
      goodToKnow={goodToKnow}
      steps={steps}
      faqs={faqs}
      ctaSubtitle="Tell us your nationality and travel dates — we'll confirm your entry route honestly and handle the paperwork if you need it."
    />
  );
}